/**
 * S-01 «Настройки» — корневая вкладка (изменение v1.2), как в Telegram.
 *
 * Сверху — шапка профиля: аватар, имя, телефон и никнейм, быстрые действия
 * «Изменить фото» и «Задать никнейм», строка «Мой профиль» (→ U-01).
 * ПС §15: аккаунт, приватность, уведомления, внешний вид/язык, безопасность,
 * заблокированные, помощь, документы, выход.
 * Критические настройки не прячутся в неочевидных меню.
 */
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { useInstallOffer } from '@features/pwa/use-install'
import { Avatar, Button, Dialog, Icon, type IconName } from '@shared/ui'

import { Screen } from '../../layouts/Screen'
import { logoutAndNavigate } from '../../boot/session-lifecycle'
import { useAppStore } from '../../model/app-store'
import { hotkeysAvailable } from '../../model/hotkeys'
import { InstallInstructions } from '../system/InstallApp'
import styles from './settings.module.css'

/** Цвет плитки значка — по образцу настроек Telegram: у каждого раздела свой. */
type Tone =
  | 'blue'
  | 'indigo'
  | 'purple'
  | 'pink'
  | 'red'
  | 'orange'
  | 'amber'
  | 'green'
  | 'teal'
  | 'sky'
  | 'gray'

const TONE_CLASS: Record<Tone, string> = {
  blue: 'tileBlue',
  indigo: 'tileIndigo',
  purple: 'tilePurple',
  pink: 'tilePink',
  red: 'tileRed',
  orange: 'tileOrange',
  amber: 'tileAmber',
  green: 'tileGreen',
  teal: 'tileTeal',
  sky: 'tileSky',
  gray: 'tileGray',
}

interface Row {
  section: string
  label: string
  icon: IconName
  tone: Tone
  value?: string
}

export function SettingsScreen(): React.ReactElement {
  const navigate = useNavigate()
  const me = useAppStore((s) => s.me)
  const [logoutOpen, setLogoutOpen] = useState(false)

  /*
   * PWA-07: постоянный пункт «Установить приложение». В standalone и после
   * установки пункт скрывается (§9.4 п. 2, ветка `appinstalled`).
   */
  const install = useInstallOffer(me?.user.id ?? null)

  // §4.6 п. 2: подсказка сочетаний доступна и из меню приложения, не только по `?`.
  const setShortcutsOpen = useAppStore((s) => s.setShortcutsOpen)
  const showHotkeys = hotkeysAvailable()

  const profile = me?.profile ?? null

  const accountRows: Row[] = [
    { section: 'account', label: 'Аккаунт', icon: 'user', tone: 'blue', value: me?.identities[0]?.masked },
    {
      section: 'identities',
      label: 'Способы входа',
      icon: 'lock',
      tone: 'indigo',
      value: `${me?.identities.length ?? 0}`,
    },
    { section: 'sessions', label: 'Активные входы', icon: 'monitor', tone: 'teal' },
    {
      section: 'restrictions',
      label: 'Ограничения аккаунта',
      icon: 'shield',
      tone: 'orange',
      value: me?.sanctions.length ? 'есть' : 'нет',
    },
  ]

  const privacyRows: Row[] = [
    { section: 'privacy', label: 'Приватность', icon: 'eye', tone: 'gray' },
    { section: 'notifications', label: 'Уведомления', icon: 'bell', tone: 'red' },
    { section: 'blocked', label: 'Заблокированные', icon: 'block', tone: 'pink' },
  ]

  const appRows: Row[] = [
    { section: 'appearance', label: 'Внешний вид и язык', icon: 'sun', tone: 'sky' },
    { section: 'support', label: 'Помощь и поддержка', icon: 'info', tone: 'green' },
    { section: 'legal', label: 'Документы', icon: 'shield', tone: 'purple' },
  ]

  const dataRows: Row[] = [
    { section: 'export', label: 'Скачать мои данные', icon: 'download', tone: 'amber' },
  ]

  const primaryIdentity = me?.identities.find((identity) => identity.is_primary) ?? me?.identities[0] ?? null

  return (
    <Screen
      title="Настройки"
      narrow
      actions={
        profile ? (
          <Button variant="ghost" size="sm" onClick={() => void navigate({ to: '/me/edit' })}>
            Изменить
          </Button>
        ) : null
      }
    >
      {/* Шапка профиля — как в настройках Telegram: всё своё начинается отсюда */}
      {profile ? (
        <div className={styles['hero']}>
          <button
            type="button"
            className={styles['heroAvatar']}
            aria-label="Мой профиль"
            onClick={() => void navigate({ to: '/me' })}
          >
            <Avatar name={profile.display_name} photo={profile.photos[0] ?? null} size="2xl" />
          </button>
          <div className={styles['heroName']}>
            {profile.display_name ?? 'Без имени'}
            {profile.age !== null ? `, ${profile.age}` : ''}
          </div>
          <div className={styles['heroMeta']}>
            {primaryIdentity ? <span>{primaryIdentity.masked}</span> : null}
            {primaryIdentity && profile.username ? <span aria-hidden> · </span> : null}
            {profile.username ? <span>@{profile.username}</span> : null}
          </div>

          <div className={`${styles['card']} ${styles['quickCard']}`}>
            <button
              type="button"
              className={styles['quickRow']}
              onClick={() => void navigate({ to: '/me/photos' })}
            >
              <span className={styles['quickIcon']}>
                <Icon name="camera" size={20} />
              </span>
              <span className={styles['quickLabel']}>Изменить фото</span>
            </button>
          </div>

          <div className={styles['card']}>
            <button type="button" className={styles['row']} onClick={() => void navigate({ to: '/me' })}>
              <span className={`${styles['rowIcon']} ${styles['tileRed']}`}>
                <Icon name="user" size={18} />
              </span>
              <span className={styles['rowBody']}>
                <span className={styles['rowLabel']}>Мой профиль</span>
                {profile.completeness.percent < 100 ? (
                  <span className={styles['rowValue']}>{profile.completeness.percent}%</span>
                ) : null}
              </span>
              <span className={styles['rowChevron']}>
                <Icon name="chevron-right" size={18} />
              </span>
            </button>
          </div>
        </div>
      ) : null}

      <SettingsGroup
        title="Аккаунт"
        rows={accountRows}
        onOpen={(s) => void navigate({ to: '/settings/$section', params: { section: s } })}
      />
      <SettingsGroup
        title="Приватность и безопасность"
        rows={privacyRows}
        onOpen={(s) => void navigate({ to: '/settings/$section', params: { section: s } })}
      />
      <SettingsGroup
        title="Приложение"
        rows={appRows}
        onOpen={(s) => void navigate({ to: '/settings/$section', params: { section: s } })}
      />
      <SettingsGroup
        title="Данные"
        rows={dataRows}
        onOpen={(s) => void navigate({ to: '/settings/$section', params: { section: s } })}
      />

      {showHotkeys ? (
        <div className={styles['group']}>
          <div className={styles['card']}>
            <button type="button" className={styles['row']} onClick={() => setShortcutsOpen(true)}>
              <span className={`${styles['rowIcon']} ${styles['tileGray']}`}>
                <Icon name="grid" size={18} />
              </span>
              <span className={styles['rowBody']}>
                <span className={styles['rowLabel']}>Горячие клавиши</span>
              </span>
              <span className={styles['rowChevron']}>
                <Icon name="chevron-right" size={18} />
              </span>
            </button>
          </div>
        </div>
      ) : null}

      {install.variant !== 'none' ? (
        <div className={styles['group']}>
          <div className={styles['card']}>
            <button
              type="button"
              className={styles['row']}
              onClick={() => {
                // В Chromium — системный диалог; в мобильной Safari — инструкция PWA-02.
                if (install.variant === 'chromium') void install.install()
                else install.openInstructions()
              }}
            >
              <span className={`${styles['rowIcon']} ${styles['tileBlue']}`}>
                <Icon name="download" size={18} />
              </span>
              <span className={styles['rowBody']}>
                <span className={styles['rowLabel']}>Установить приложение</span>
              </span>
              <span className={styles['rowChevron']}>
                <Icon name="chevron-right" size={18} />
              </span>
            </button>
          </div>
        </div>
      ) : null}

      <InstallInstructions
        open={install.instructionsOpen}
        onClose={install.closeInstructions}
        onAlreadyInstalled={() => {
          install.hideForever()
          install.closeInstructions()
        }}
      />

      <div className={styles['group']}>
        <div className={styles['card']}>
          <button
            type="button"
            className={`${styles['row']} ${styles['rowDanger']}`}
            onClick={() => setLogoutOpen(true)}
          >
            <span className={styles['rowIcon']}>
              <Icon name="logout" size={18} />
            </span>
            <span className={styles['rowBody']}>
              <span className={styles['rowLabel']}>Выйти</span>
            </span>
          </button>

          <button
            type="button"
            className={`${styles['row']} ${styles['rowDanger']}`}
            onClick={() => void navigate({ to: '/settings/$section', params: { section: 'delete' } })}
          >
            <span className={styles['rowIcon']}>
              <Icon name="trash" size={18} />
            </span>
            <span className={styles['rowBody']}>
              <span className={styles['rowLabel']}>Удалить аккаунт</span>
            </span>
          </button>
        </div>
      </div>

      <p className={styles['version']}>Движ · веб-клиент {__APP_VERSION__}</p>

      {/* S-24: выход — подтверждаем, чтобы не потерять черновики случайно */}
      <Dialog
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        title="Выйти из аккаунта?"
        description="Локальные данные этого устройства будут удалены. Неотправленные сообщения пропадут."
        footer={
          <>
            <Button variant="secondary" onClick={() => setLogoutOpen(false)}>
              Остаться
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setLogoutOpen(false)
                void logoutAndNavigate(() => navigate({ to: '/', replace: true }))
              }}
            >
              Выйти
            </Button>
          </>
        }
      />
    </Screen>
  )
}

function SettingsGroup({
  title,
  rows,
  onOpen,
}: {
  title: string
  rows: Row[]
  onOpen: (section: string) => void
}) {
  return (
    <section className={styles['group']}>
      <h2 className={styles['groupTitle']}>{title}</h2>

      <div className={styles['card']}>
        {rows.map((row) => (
          <button
            key={row.section}
            type="button"
            className={styles['row']}
            onClick={() => onOpen(row.section)}
          >
            <span className={`${styles['rowIcon']} ${styles[TONE_CLASS[row.tone]]}`}>
              <Icon name={row.icon} size={18} />
            </span>

            <span className={styles['rowBody']}>
              <span className={styles['rowLabel']}>{row.label}</span>
              {row.value ? <span className={styles['rowValue']}>{row.value}</span> : null}
            </span>

            <span className={styles['rowChevron']}>
              <Icon name="chevron-right" size={18} />
            </span>
          </button>
        ))}
      </div>
    </section>
  )
}
